import { SpinnerOutlineIcon } from '@applyboard/ui-icons';
import React, { useContext } from 'react';
import { css, keyframes } from '@emotion/react';

import { IconWrapper, IconWrapperForButtonProps } from '../IconWrapper';
import { LinkCompatibleProps } from '../InternalLink';
import { InternalText } from '../InternalText';
import { TooltipCompatibleProps } from '../InternalTooltip';

import {
  BorderRadiusToken,
  FullTheme,
  GradientColorToken,
  IconType,
  Intent,
  IntentProps,
  MarginProps,
  useFullTheme,
} from '~/design';

import { Breakpoint, Responsive } from '~/design/breakpoint';
import { useMarginStyle, useResponsive, useResponsiveHideStyle } from '~/utils/styling';
import { allowedTooltipSymbols } from '~/utils/tooltip';

import { useBadgeContext } from '../BadgeContext';
import { allowedBadgeSymbols } from '~/utils/badge';
import { BackgroundContext } from '../BackgroundContext';
import { useGradient } from '~/utils';

export interface InternalButtonProps extends IntentProps, TooltipCompatibleProps, MarginProps {
  /** The aria-controls of the button. Use this to indicate what element is controlled by this
   * button. */
  'aria-controls'?: string;

  /** The aria-current of the button. When there is a list or group of buttons, use this to indicate
   * if this is the currently selected button. */
  'aria-current'?: 'true' | 'false';

  /** The aria-expanded of the button. Use this to indicate if the element controlled by this button
   * is expanded or not. */
  'aria-expanded'?: boolean;

  /** The aria-haspopup of the button. Use this to indicate if the button triggers a popup element. */
  'aria-haspopup'?: 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog';

  /** The aria-label of the button.  This should only be used for icon-only buttons, or for when the
   * button text doesn't represent the "intention" of the button. */
  'aria-label'?: string;

  /** The aria-live of the button. */
  'aria-live'?: 'off' | 'assertive' | 'polite';

  as?: React.ElementType<LinkCompatibleProps>;

  /** The text of the button. */
  children?: React.ReactNode;

  /** How will the button content be displayed. */
  contentAlign?: 'center' | 'full';

  /** If true, the button cannot be interacted with. */
  disabled?: boolean;

  /** If specified, the browser will treat the linked URL as a download, not a page. To learn more, see [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#download). */
  download?: string | boolean;

  /** If true apply a style to show that the button is elevated compared to the content around it */
  elevated?: boolean;

  /** The emphasis of the button against its background. (default: contained) */
  emphasis?: 'contained' | 'outlined' | 'highlighted' | 'transparent';

  /** Associates this button with a form to submit */
  form?: string;

  /** Adds a predefined gradient as the button background color. When set, `intent` and `emphasis` are ignored. */
  gradient?: GradientColorToken;

  /** Hides the button above the provided breakpoint size */
  hideAbove?: Breakpoint;

  /** Hides the button below the provided breakpoint size */
  hideBelow?: Breakpoint;

  href?: string;
  onAfterHistoryPush?: () => void;

  /** The icon that appears on the left side of the button. */
  leadIcon?: IconType;

  /** If true, the button's contents will be replaced with a loading spinner. */
  loading?: boolean;

  /** The ID of the button. */
  id?: string;

  /** A callback function that is invoked when the button is clicked. */
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void;

  /** The rel of the element. Only applies when rendered as a link. (default: 'noopener') */
  rel?: string;

  /** The size of the button. */
  size?: 'sm' | 'md' | 'lg';

  target?: '_blank';

  /** The icon that appears on the right side of the button. */
  trailIcon?: IconType;

  /** Whether the button submits the form or not. Unlike the native HTML spec, it defaults to
   * `button` instead of `submit` to prevent multiple submit buttons in a form. `reset` won'
   * be a valid option until we have a good reason to add it because resetting forms tends to
   * annoy users and it doesn't go well with controlled components. */
  type?: 'button' | 'submit';

  /**
   * The display variation of the button.
   * @deprecated Use `emphasis` instead.
   */
  variant?: 'contained' | 'outlined' | 'highlighted' | 'transparent';

  /** The width of the button. */
  width?: 'hug' | 'fill' | Responsive<'hug' | 'fill'>;
}

export const InternalButton = React.forwardRef<HTMLButtonElement, InternalButtonProps>(
  function Button(
    {
      'aria-controls': ariaControls,
      'aria-current': ariaCurrent,
      'aria-describedby': ariaDescribedby,
      'aria-expanded': ariaExpanded,
      'aria-haspopup': ariaHasPopup,
      'aria-label': ariaLabelProp,
      'aria-live': ariaLive,
      as,
      children,
      contentAlign = 'center',
      disabled = false,
      download,
      elevated = false,
      emphasis: _emphasis = 'contained',
      form,
      gradient,
      hideAbove,
      hideBelow,
      href,
      id,
      intent = 'secondary',
      leadIcon: LeadIcon,
      loading = false,
      m,
      mb,
      ml,
      mr,
      mt,
      mx,
      my,
      onAfterHistoryPush,
      onBlur,
      onClick,
      onFocus,
      onPointerDown,
      onPointerLeave,
      onPointerMove,
      rel = 'noopener',
      size = 'md',
      target,
      trailIcon: TrailIcon,
      type = 'button',
      variant,
      width = 'hug',
    },
    ref,
  ) {
    if (!['button', 'submit'].includes(type)) {
      throw new Error('`type` prop must be set to either `button` or `submit`.');
    }

    if (type !== 'submit' && typeof form === 'string') {
      throw new Error("`form` prop can only be set if `type` prop is set to `'submit'`");
    }

    const isIconOnly =
      (!children || React.Children.toArray(children).filter(Boolean).length === 0) && !!LeadIcon;
    if (isIconOnly && !ariaLabelProp) {
      throw new Error('`aria-label` must be supplied for icon-only buttons.');
    }

    const badgeContext = useBadgeContext();

    const buttonAriaDescribedBy = badgeContext.ariaDescribedBy
      ? `${ariaDescribedby ?? ''} ${badgeContext.ariaDescribedBy}`.trim()
      : ariaDescribedby;

    const theme = useFullTheme();
    const responsive = useResponsive();
    const gradientBg = useGradient(gradient);

    const emphasis = gradient ? 'contained' : variant ?? _emphasis;

    const backgroundContext = React.useContext(BackgroundContext);
    const contrast =
      backgroundContext?.isBackgroundGradient &&
      (emphasis === 'outlined' || emphasis === 'transparent')
        ? 'inverted'
        : 'mid';

    const isDisabled = disabled || loading;
    const ariaLabel = loading ? 'Loading' : ariaLabelProp ? ariaLabelProp : undefined;
    const isLink = !!as;

    const baseStyle = useButtonBaseStyle({ contrast, elevated, intent, width });
    const emphasisStyle = useButtonEmphasisStyle({ contrast, emphasis, intent });
    const sizeStyle = useButtonSizeStyle({ isIconOnly, size });

    const disabledBgStyle = {
      contained: css({
        backgroundColor: theme.colors.actionInactive,
      }),
      outlined: css({
        ...theme.borders.inactive,
        backgroundColor: contrast === 'inverted' ? 'transparent' : theme.colors.bgInactive,
      }),
      highlighted: css({
        backgroundColor: theme.colors.bgInactive,
      }),
      transparent: css({
        backgroundColor: 'transparent',
      }),
    }[emphasis];

    const disabledStyle = css([
      disabledBgStyle,
      {
        cursor: 'not-allowed',
      },
    ]);

    const linkStyle = {
      alignItems: 'center',
      display: 'inline-flex',
      justifyContent: 'center',
      textDecoration: 'none',
    };

    const marginStyle = useMarginStyle({
      m,
      mx,
      my,
      mt,
      mr,
      mb,
      ml,
    });

    const iconProps: IconWrapperForButtonProps = {
      contrast: emphasis === 'contained' ? 'inverted' : intent === 'secondary' ? 'high' : 'mid',
      disabled,
      intent: intent,
      saliency: contrast === 'inverted' ? 'inverted' : 'default',
      selector: isLink ? 'a' : 'button',
    };

    const Tag = isLink ? as : 'button';
    const onClickWrapper = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        if (isLink) {
          onClick?.();
        } else {
          if (!isDisabled) {
            onClick?.(e);
          }
        }
      },
      [isLink, isDisabled, onClick],
    );

    const hideStyle = useResponsiveHideStyle({ hideBelow, hideAbove });

    const textContrast =
      emphasis === 'contained'
        ? 'inverted'
        : isDisabled
          ? 'low'
          : // If the emphasis is not contained and the intent is secondary we need to use high contrast
            // otherwise just us the mid toned intent text color
            intent === 'secondary'
            ? 'high'
            : 'mid';

    const invertedContrastTextStyle = css({
      color: theme.colors.textInverted,
      '&[aria-disabled=false]:hover div': {
        color: {
          magic: theme.colors.textMagicHover,
          negative: theme.colors.textNegativeHover,
          positive: theme.colors.textPositiveHover,
          primary: theme.colors.textPrimaryHover,
          secondary: theme.colors.textSecondaryStarkHover,
          warning: theme.colors.textWarningHover,
        }[intent],
      },
      '&[aria-disabled=false]:active div': {
        color: {
          magic: theme.colors.textMagicPressed,
          negative: theme.colors.textNegativePressed,
          positive: theme.colors.textPositivePressed,
          primary: theme.colors.textPrimaryPressed,
          secondary: theme.colors.textSecondaryStarkPressed,
          warning: theme.colors.textWarningPressed,
        }[intent],
      },
      '&[aria-disabled=true] div': {
        color: theme.colors.textInactive,
      },
    });

    // If gradient is set, override background style
    const gradientStyle = gradientBg
      ? css({
          background: `linear-gradient(90deg, ${gradientBg[0]}, ${gradientBg[1]})`,
          // Some color combinations create an horizontal visual artifact that seems to not extend the gradient all the way
          // So this compensates it by adding a small offset to the left
          backgroundSize: 'calc(100% + 20px)',
          backgroundPositionX: '-10px',
        })
      : undefined;

    return (
      <Tag
        aria-controls={ariaControls}
        aria-current={ariaCurrent}
        aria-describedby={buttonAriaDescribedBy}
        aria-disabled={isDisabled}
        aria-expanded={ariaExpanded}
        aria-haspopup={ariaHasPopup}
        aria-label={ariaLabel}
        aria-live={ariaLive}
        css={[
          baseStyle,
          emphasisStyle,
          sizeStyle,
          isDisabled && disabledStyle,
          marginStyle,
          isLink && linkStyle,
          hideStyle,
          contrast === 'inverted' && invertedContrastTextStyle,
          gradientStyle,
          // TODO: Add gradient interaction styles (hover, active, focus) here in the future
        ]}
        {...(onAfterHistoryPush ? { onAfterHistoryPush } : {})}
        // @ts-expect-error `href` should be provided for links but `undefined` for buttons
        href={isLink ? href ?? '' : undefined}
        id={id}
        onBlur={onBlur}
        // @ts-expect-error LinkAs wants `() => void`, button wants `(e) => void`
        onClick={onClickWrapper}
        onFocus={onFocus}
        onPointerDown={onPointerDown}
        onPointerLeave={onPointerLeave}
        onPointerMove={onPointerMove}
        // @ts-expect-error ref can be for either a link element or a button element
        ref={ref}
        rel={rel}
        target={target}
        type={loading || disabled ? 'button' : type}
        form={form}
        download={download}
      >
        <div
          aria-hidden={loading ? 'true' : undefined}
          css={[
            {
              alignItems: 'center',
              display: 'flex',
              minWidth: '100%',
              visibility: loading ? 'hidden' : undefined,
            },
            responsive.css({
              gap: responsive.map(width, prop =>
                TrailIcon && (!LeadIcon || prop !== 'fill') ? theme.spacing[4] : 0,
              ),
              justifyContent: responsive.map(width, prop =>
                LeadIcon && TrailIcon && prop === 'fill' ? 'space-between' : 'center',
              ),
            }),
          ]}
        >
          <div
            css={{
              display: 'flex',
              flexGrow: contentAlign === 'full' ? 1 : 0,
              gap: isIconOnly ? 0 : theme.spacing[4],
            }}
          >
            {LeadIcon && (
              <IconWrapper {...iconProps}>
                <LeadIcon />
              </IconWrapper>
            )}

            <InternalText
              as="span"
              intent={intent}
              contrast={contrast === 'mid' ? textContrast : undefined}
              variant={size === 'sm' ? 'labelM' : 'labelL'}
              color={contrast === 'mid' ? undefined : 'inherit'}
            >
              {children}
            </InternalText>
          </div>

          {!isIconOnly && TrailIcon && (
            <IconWrapper {...iconProps}>
              <TrailIcon />
            </IconWrapper>
          )}
        </div>

        {loading && (
          <div
            css={{
              alignItems: 'center',
              display: 'flex',
              height: '100%',
              justifyContent: 'center',
              left: 0,
              position: 'absolute',
              top: 0,
              width: '100%',
            }}
          >
            <span
              css={{
                animationDirection: 'normal',
                animationDuration: '0.8s',
                animationIterationCount: 'infinite',
                animationName: loadingAnimation,
                animationTimingFunction: 'linear',
                height: '24px',
              }}
            >
              <IconWrapper
                contrast={emphasis === 'contained' ? 'inverted' : 'mid'}
                intent="primary"
                selector={isLink ? 'a' : 'button'}
                saliency={contrast === 'inverted' ? 'inverted' : 'default'}
              >
                <SpinnerOutlineIcon />
              </IconWrapper>
            </span>
          </div>
        )}
      </Tag>
    );
  },
);

Object.assign(InternalButton, {
  [allowedBadgeSymbols.Button]: true,
  [allowedTooltipSymbols.Button]: true,
});

const loadingAnimation = keyframes`
  0% {
    transform: rotate(-90deg);
  }
  100% {
    transform: rotate(270deg);
  }
`;

const lightningGradient =
  'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.2) 50%, transparent 100%)';

const lightningAnimation = keyframes`
  0% {
    background: ${lightningGradient};
    transform: translateX(-100%);
  }
  100% {
    background: ${lightningGradient};
    transform: translateX(100%);
  }
`;

export function useButtonBaseStyle({
  allowInteractions = true,
  borderRadius = 'control',
  contrast = 'mid',
  cursor = 'pointer',
  elevated = false,
  intent = 'secondary',
  width = 'hug',
}: {
  allowInteractions?: boolean;
  borderRadius?: BorderRadiusToken;
  contrast?: 'mid' | 'inverted';
  cursor?: 'pointer' | 'help';
  elevated?: InternalButtonProps['elevated'];
  intent?: InternalButtonProps['intent'];
  width?: InternalButtonProps['width'];
}) {
  const theme = useFullTheme();
  const { backgroundColor } = useContext(BackgroundContext);
  const responsive = useResponsive();

  const { duration, easing, translateY } = theme.animation || {};

  const transition =
    duration && easing && translateY
      ? `transform ${duration} ${easing}, box-shadow ${duration} ${easing}, background-color ${duration} ${easing}`
      : undefined;

  return css([
    {
      appearance: 'none',
      borderRadius: theme.borderRadiuses[borderRadius],
      boxShadow: elevated ? theme.elevations.z1 : 'none',
      minWidth: 'unset',
      overflow: 'hidden',
      position: 'relative',
      // In practice you pretty much never want buttons to wrap and become
      // taller.  We can revisit this decision later by adding a prop to
      // control this, but I think this is a good default
      whiteSpace: 'nowrap',
      '@media (prefers-reduced-motion: no-preference)': {
        transition,
      },
    },
    allowInteractions && {
      // Do not change the ordering of these pseudo selectors!
      '&:hover': {
        boxShadow: elevated ? theme.elevations.z2 : theme.elevations.z1,
        cursor,
        transform: transition ? translateY : undefined,
        '&::before': {
          content: transition ? '""' : undefined,
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          borderRadius: 'inherit',
          overflow: 'hidden',
          transform: 'translateX(-100%)',
          background:
            'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.2) 50%, transparent 100%)',
          '@media (prefers-reduced-motion: no-preference)': {
            transition: 'transform 0.5s ease-in-out',
          },
        },
        '&:hover::before': {
          '@media (prefers-reduced-motion: no-preference)': {
            animation: `${lightningAnimation} 0.5s ease-in-out forwards`,
          },
        },
        '&:not(:hover)::before': {
          transform: 'translateX(100%)',
        },
      },
      '&:active': {
        boxShadow: elevated ? theme.elevations.z1 : 'none',
      },
      '&:focus-visible': {
        borderRadius: theme.borderRadiuses.control,
        outline: `3px solid ${getOutlineColor({ intent, contrast, backgroundColor, theme })}`,
        outlineOffset: '1px',
      },
      '&[aria-disabled=true]': {
        boxShadow: elevated ? theme.elevations.z1 : 'none',
        cursor: 'not-allowed',
      },
    },
    responsive.css({
      width: responsive.map(width ?? 'hug', prop => (prop === 'fill' ? '100%' : 'fit-content')),
    }),
  ]);
}

export function useButtonSizeStyle({
  isIconOnly = false,
  size = 'md',
}: {
  isIconOnly?: boolean;
  size?: InternalButtonProps['size'];
}) {
  const theme = useFullTheme();
  return {
    sm: {
      height: theme.spacing[10],
      paddingBlock: theme.spacing[0],
      paddingInline: isIconOnly ? theme.spacing[0] : theme.spacing[4],
      width: isIconOnly ? theme.spacing[10] : undefined,
    },
    md: {
      height: theme.spacing[12],
      paddingBlock: theme.spacing[0],
      paddingInline: isIconOnly ? theme.spacing[0] : theme.spacing[4],
      width: isIconOnly ? theme.spacing[12] : undefined,
    },
    lg: {
      height: theme.spacing[14],
      paddingBlock: theme.spacing[0],
      paddingInline: isIconOnly ? theme.spacing[0] : theme.spacing[4],
      width: isIconOnly ? theme.spacing[14] : undefined,
    },
  }[size ?? 'md'];
}

export function useButtonEmphasisStyle({
  allowInteractions = true,
  borderless: _borderless = [],
  contrast = 'mid',
  emphasis = 'contained',
  intent = 'secondary',
  useEnabledPseudoClass = true,
}: {
  allowInteractions?: boolean;
  borderless?: Array<'left' | 'right'>;
  contrast?: 'mid' | 'inverted';
  emphasis?: InternalButtonProps['emphasis'];
  intent?: InternalButtonProps['intent'];
  useEnabledPseudoClass?: boolean;
}) {
  const theme = useFullTheme();
  const borderless = _borderless ?? [];

  const outlinedBorder =
    contrast === 'mid'
      ? theme.borders[intent]
      : {
          ...theme.borders[intent],
          borderColor: theme.colors.borderInverted,
        };

  const pseudoClassPrefix = useEnabledPseudoClass ? `&[aria-disabled=false]` : '&';

  const highlightedBg = {
    default: {
      magic: theme.colors.bgMagicHighlight,
      negative: theme.colors.bgNegativeHighlight,
      positive: theme.colors.bgPositiveHighlight,
      primary: theme.colors.bgPrimaryHighlight,
      secondary: theme.colors.bgSecondaryHighlight,
      warning: theme.colors.bgWarningHighlight,
    },
    hover: {
      magic: theme.colors.bgMagicHighlightHover,
      negative: theme.colors.bgNegativeHighlightHover,
      positive: theme.colors.bgPositiveHighlightHover,
      primary: theme.colors.bgPrimaryHighlightHover,
      secondary: theme.colors.bgSecondaryHighlightHover,
      warning: theme.colors.bgWarningHighlightHover,
    },
    active: {
      magic: theme.colors.bgMagicHighlightPressed,
      negative: theme.colors.bgNegativeHighlightPressed,
      positive: theme.colors.bgPositiveHighlightPressed,
      primary: theme.colors.bgPrimaryHighlightPressed,
      secondary: theme.colors.bgSecondaryHighlightPressed,
      warning: theme.colors.bgWarningHighlightPressed,
    },
  };

  return {
    contained: css([
      {
        ...theme.borders.transparent,
        backgroundColor: {
          magic: theme.colors.actionMagic,
          negative: theme.colors.actionNegative,
          positive: theme.colors.actionPositive,
          primary: theme.colors.actionPrimary,
          secondary: theme.colors.actionSecondary,
          warning: theme.colors.actionWarning,
        }[intent],
      },
      allowInteractions && {
        [`${pseudoClassPrefix}:hover`]: {
          backgroundColor: {
            magic: theme.colors.actionMagicHover,
            negative: theme.colors.actionNegativeHover,
            positive: theme.colors.actionPositiveHover,
            primary: theme.colors.actionPrimaryHover,
            secondary: theme.colors.actionSecondaryHover,
            warning: theme.colors.actionWarningHover,
          }[intent],
        },
        [`${pseudoClassPrefix}:active`]: {
          backgroundColor: {
            magic: theme.colors.actionMagicPressed,
            negative: theme.colors.actionNegativePressed,
            positive: theme.colors.actionPositivePressed,
            primary: theme.colors.actionPrimaryPressed,
            secondary: theme.colors.actionSecondaryPressed,
            warning: theme.colors.actionWarningPressed,
          }[intent],
        },
      },
    ]),
    outlined: css([
      {
        ...outlinedBorder,
        backgroundColor: contrast === 'mid' ? highlightedBg.default[intent] : 'transparent',
      },
      borderless.includes('left') && {
        borderLeft: 'none',
      },
      borderless.includes('right') && {
        borderRight: 'none',
      },
      allowInteractions && {
        [`${pseudoClassPrefix}:hover`]: {
          borderColor: theme.borders[intent].borderColor,
          backgroundColor: highlightedBg.hover[intent],
        },
        [`${pseudoClassPrefix}:active`]: {
          borderColor: theme.borders[intent].borderColor,
          backgroundColor: highlightedBg.active[intent],
        },
      },
    ]),
    highlighted: css([
      {
        ...theme.borders.transparent,
        backgroundColor: highlightedBg.default[intent],
      },
      allowInteractions && {
        [`${pseudoClassPrefix}:hover`]: {
          backgroundColor: highlightedBg.hover[intent],
        },
        [`${pseudoClassPrefix}:active`]: {
          backgroundColor: highlightedBg.active[intent],
        },
      },
    ]),
    transparent: css([
      {
        ...theme.borders.transparent,
        backgroundColor: 'transparent',
      },
      allowInteractions && {
        [`${pseudoClassPrefix}:hover`]: {
          backgroundColor: highlightedBg.hover[intent],
        },
        [`${pseudoClassPrefix}:active`]: {
          backgroundColor: highlightedBg.active[intent],
        },
      },
    ]),
  }[emphasis];
}

function getOutlineColor({
  intent,
  contrast,
  backgroundColor,
  theme,
}: {
  intent: Intent;
  contrast: 'inverted' | 'mid';
  backgroundColor: string | [string, string] | undefined;
  theme: FullTheme;
}) {
  if (contrast === 'inverted') {
    return theme.colors.actionInverted;
  }

  const intentOutlineMapping: Record<Intent, string> = {
    magic: theme.colors.actionMagic,
    negative: theme.colors.actionNegative,
    positive: theme.colors.actionPositive,
    primary:
      backgroundColor === theme.colors.actionPrimary
        ? theme.colors.bgPrimaryPressed
        : theme.colors.actionPrimary,
    secondary: theme.colors.actionPrimary,
    warning: theme.colors.actionWarning,
  };

  return intentOutlineMapping[intent];
}
